import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";

export default function HoursDialog({ open, onOpenChange, jobId, onSaved }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [hours, setHours] = useState("8");
  const [notes, setNotes] = useState("");
  const [userId, setUserId] = useState("");
  const [operators, setOperators] = useState([]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDate(new Date().toISOString().slice(0, 10));
    setHours("8");
    setNotes("");
    setError("");
    setUserId("");
    if (isAdmin) {
      api.get("/users").then(({ data }) => {
        setOperators(data);
      }).catch(() => setOperators([]));
    }
  }, [open, isAdmin]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      const payload = { date, hours: parseFloat(hours), notes };
      if (isAdmin && userId) payload.user_id = userId;
      const { data } = await api.post(`/jobs/${jobId}/hours`, payload);
      onSaved?.(data);
      onOpenChange(false);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="hours-dialog">
        <DialogHeader>
          <DialogTitle className="text-brand-navy">Registra ore lavorate</DialogTitle>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="date">Data</Label>
              <Input id="date" type="date" required value={date}
                onChange={(e) => setDate(e.target.value)}
                data-testid="hours-date-input" />
            </div>
            <div>
              <Label htmlFor="hours">Ore</Label>
              <Input id="hours" type="number" step="0.25" min="0" max="24" required value={hours}
                onChange={(e) => setHours(e.target.value)}
                data-testid="hours-hours-input" />
            </div>
          </div>

          {isAdmin && (
            <div>
              <Label>Operatore (opzionale)</Label>
              <Select value={userId} onValueChange={setUserId}>
                <SelectTrigger data-testid="hours-user-select">
                  <SelectValue placeholder="Me stesso" />
                </SelectTrigger>
                <SelectContent>
                  {operators.map((o) => (
                    <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label htmlFor="notes">Note</Label>
            <Textarea id="notes" value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Attività svolte..." rows={3}
              data-testid="hours-notes-input" />
          </div>

          {error && <div className="text-sm text-red-600" data-testid="hours-error">{error}</div>}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annulla
            </Button>
            <Button type="submit" disabled={saving} className="brand-navy text-white"
              data-testid="hours-submit-button">
              {saving ? "Salvataggio..." : "Salva"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
